import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface Variant {
  id: string;
  stepId: string;
  label: string;
  weight: number;
  subjectTemplate: string;
  bodyTemplate: string;
}

interface Props {
  campaignId: string;
  stepId: string;
  isEmail: boolean;
}

/**
 * M5 Variants editor. Renders inline below the email body inside the step
 * dialog when editing an existing step. Up to 4 variants. Empty = step's
 * own subject/body is used (single-variant control). Each variant has a
 * weight (relative integer, normalized server-side for the picker).
 */
export function DripStepVariantsEditor({ campaignId, stepId, isEmail }: Props) {
  const { toast } = useToast();
  const [draft, setDraft] = useState({ label: "Variant", subjectTemplate: "", bodyTemplate: "", weight: 1 });

  const queryKey = ["/api/drip-campaigns", campaignId, "steps", stepId, "variants"] as const;
  const { data: variants = [], isLoading } = useQuery<Variant[]>({ queryKey });

  const createMut = useMutation({
    mutationFn: async (payload: typeof draft) => {
      return await apiRequest(
        "POST",
        `/api/drip-campaigns/${campaignId}/steps/${stepId}/variants`,
        payload,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setDraft({ label: "Variant", subjectTemplate: "", bodyTemplate: "", weight: 1 });
    },
    onError: (e: any) => toast({ title: "Couldn't add variant", description: e?.message, variant: "destructive" }),
  });

  const updateMut = useMutation({
    mutationFn: async ({ id, ...patch }: Partial<Variant> & { id: string }) => {
      return await apiRequest(
        "PUT",
        `/api/drip-campaigns/${campaignId}/steps/${stepId}/variants/${id}`,
        patch,
      );
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      return await apiRequest(
        "DELETE",
        `/api/drip-campaigns/${campaignId}/steps/${stepId}/variants/${id}`,
      );
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  if (isLoading) {
    return <p className="text-xs text-muted-foreground" data-testid="text-variants-loading">Loading variants…</p>;
  }

  const totalWeight = variants.reduce((acc, v) => acc + Math.max(1, v.weight || 1), 0);

  return (
    <div className="space-y-3 p-3 rounded-md border bg-muted/20" data-testid="section-variants">
      <div className="flex items-start justify-between">
        <div>
          <Label className="text-sm font-semibold">A/B Variants</Label>
          <p className="text-xs text-muted-foreground mt-0.5">
            Add up to 4 variants. Each enrollee deterministically gets one based on weight. Leave empty to send the step's own message.
          </p>
        </div>
        <Badge variant="outline" data-testid="badge-variant-count">{variants.length}/4</Badge>
      </div>

      {variants.length > 0 && (
        <div className="space-y-3">
          {variants.map((v) => {
            const sharePct = totalWeight > 0 ? Math.round((Math.max(1, v.weight) / totalWeight) * 100) : 0;
            return (
              <div key={v.id} className="space-y-2 p-2 rounded border bg-background" data-testid={`row-variant-${v.id}`}>
                <div className="flex items-center gap-2">
                  <Input
                    value={v.label}
                    onChange={(e) => updateMut.mutate({ id: v.id, label: e.target.value })}
                    placeholder="Variant label"
                    className="flex-1"
                    data-testid={`input-variant-label-${v.id}`}
                  />
                  <div className="flex items-center gap-1">
                    <Label className="text-xs whitespace-nowrap">Weight</Label>
                    <Input
                      type="number"
                      min={1}
                      value={v.weight}
                      onChange={(e) => updateMut.mutate({ id: v.id, weight: Math.max(1, Number(e.target.value) || 1) })}
                      className="w-20"
                      data-testid={`input-variant-weight-${v.id}`}
                    />
                    <Badge variant="secondary" className="ml-1" data-testid={`text-variant-share-${v.id}`}>{sharePct}%</Badge>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteMut.mutate(v.id)}
                    data-testid={`button-delete-variant-${v.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                {isEmail && (
                  <Input
                    value={v.subjectTemplate}
                    onChange={(e) => updateMut.mutate({ id: v.id, subjectTemplate: e.target.value })}
                    placeholder="Subject (leave blank to use step subject)"
                    data-testid={`input-variant-subject-${v.id}`}
                  />
                )}
                <Textarea
                  value={v.bodyTemplate}
                  onChange={(e) => updateMut.mutate({ id: v.id, bodyTemplate: e.target.value })}
                  placeholder={isEmail ? "Body HTML (leave blank to use step body)" : "SMS body"}
                  rows={3}
                  data-testid={`input-variant-body-${v.id}`}
                />
              </div>
            );
          })}
        </div>
      )}

      {variants.length < 4 && (
        <div className="space-y-2 p-2 rounded border border-dashed">
          <div className="flex items-center gap-2">
            <Input
              value={draft.label}
              onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              placeholder="Variant label"
              className="flex-1"
              data-testid="input-new-variant-label"
            />
            <Input
              type="number"
              min={1}
              value={draft.weight}
              onChange={(e) => setDraft({ ...draft, weight: Math.max(1, Number(e.target.value) || 1) })}
              className="w-20"
              data-testid="input-new-variant-weight"
            />
          </div>
          {isEmail && (
            <Input
              value={draft.subjectTemplate}
              onChange={(e) => setDraft({ ...draft, subjectTemplate: e.target.value })}
              placeholder="Subject (optional)"
              data-testid="input-new-variant-subject"
            />
          )}
          <Textarea
            value={draft.bodyTemplate}
            onChange={(e) => setDraft({ ...draft, bodyTemplate: e.target.value })}
            placeholder={isEmail ? "Body HTML (optional)" : "SMS body"}
            rows={2}
            data-testid="input-new-variant-body"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => createMut.mutate(draft)}
            disabled={createMut.isPending}
            data-testid="button-add-variant"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> Add variant
          </Button>
        </div>
      )}
    </div>
  );
}
